/**
 * Kalaboost — cadeau brosse + order bump.
 *
 * Se greffe sur shopify:cart:lines-update (Horizon).
 * Les IDs de variantes viennent uniquement de #kalaboost-cart-brush-config.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const CONFIG_SELECTOR = '#kalaboost-cart-brush-config';
const BUMP_SELECTOR = '[data-kb-brush-bump]';
const BUMP_ADD_SELECTOR = '[data-kb-brush-bump-add]';
const SOURCE = 'kalaboost-brush';

/**
 * @returns {object | null}
 */
function readConfig() {
  const node = document.querySelector(CONFIG_SELECTOR);
  if (!node?.textContent) return null;

  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    console.warn('[kalaboost-brush] Config JSON invalide', error);
    return null;
  }
}

/**
 * @param {number} count
 * @returns {Promise<void>}
 */
function waitFrames(count = 2) {
  return new Promise((resolve) => {
    const step = (left) => {
      if (left <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => step(left - 1));
    };
    step(count);
  });
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function toId(value) {
  return Number(value) || 0;
}

class KalaboostCartBrush {
  /** @type {object | null} */
  #config = null;

  /** @type {boolean} */
  #syncing = false;

  /** @type {boolean} */
  #dirty = false;

  /** @type {boolean} */
  #internalMutation = false;

  /** @type {Promise<void>} */
  #queue = Promise.resolve();

  constructor() {
    this.#config = readConfig();
    if (!this.#isConfigured()) return;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.addEventListener('click', this.#onClick);
    this.#enqueue(() => this.#sync());
  }

  #isConfigured() {
    const config = this.#config;
    return Boolean(config?.enabled && config.productHandle && toId(config.variantIds?.gift) && toId(config.variantIds?.bump));
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    // Ignore les événements que nous déclenchons pendant une mutation.
    if (this.#internalMutation) return;
    if (event.detail?.source === SOURCE) return;

    event.promise
      ?.then(async () => {
        await waitFrames(2);
        await this.#enqueue(() => this.#sync());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[kalaboost-brush] Événement panier rejeté', error);
        }
      });
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest(BUMP_ADD_SELECTOR) : null;
    if (!button) return;

    event.preventDefault();
    if (button.hasAttribute('aria-busy')) return;

    this.#enqueue(() => this.#addBump(button));
  };

  /**
   * @param {() => Promise<void>} task
   */
  #enqueue(task) {
    this.#queue = this.#queue.then(task).catch((error) => {
      console.warn('[kalaboost-brush] Tâche file d’attente', error);
    });
    return this.#queue;
  }

  /**
   * Calcule l’état puis applique au plus une mutation, jusqu’à stabilité.
   */
  async #sync() {
    if (!this.#isConfigured()) return;

    if (this.#syncing) {
      this.#dirty = true;
      return;
    }

    this.#syncing = true;
    this.#dirty = false;

    try {
      let cart = await this.#fetchCart();
      let guard = 0;

      while (guard < 6) {
        guard += 1;
        const plan = this.#plan(cart);
        this.#updateBumpUi(plan);

        if (plan.type === 'noop') break;

        cart = await this.#execute(plan);
      }
    } finally {
      this.#syncing = false;
      if (this.#dirty) {
        this.#dirty = false;
        await this.#sync();
      }
    }
  }

  /**
   * @param {object} cart
   */
  #plan(cart) {
    const snapshot = this.#snapshot(cart);
    const qualified = snapshot.hasPackExpert || snapshot.baseCalcul >= snapshot.thresholdCents;
    const state = qualified ? 'OFFERTE' : 'PAYANTE';

    if (state === 'OFFERTE') {
      if (snapshot.bumpLines.length > 0) {
        return { type: 'remove-bump', state, snapshot };
      }
      if (snapshot.giftQuantity === 0) {
        return { type: 'add-gift', state, snapshot };
      }
      if (snapshot.giftQuantity !== 1) {
        return { type: 'clamp-gift', state, snapshot };
      }
    } else if (snapshot.giftLines.length > 0) {
      return { type: 'remove-gift', state, snapshot };
    }

    return { type: 'noop', state, snapshot };
  }

  /**
   * Sous-total hors brosse + présence Pack Expert / lignes brosse.
   * @param {object} cart
   */
  #snapshot(cart) {
    const config = this.#config;
    const brushHandle = String(config.productHandle || '');
    const packHandle = String(config.packHandle || '');
    const giftId = toId(config.variantIds.gift);
    const bumpId = toId(config.variantIds.bump);
    const standardId = toId(config.variantIds.standard);
    const brushVariantIds = new Set([giftId, bumpId, standardId].filter(Boolean));
    const giftProperty = config.giftProperty || '_offert';

    /** @type {object[]} */
    const items = Array.isArray(cart?.items) ? cart.items : [];
    /** @type {object[]} */
    const giftLines = [];
    /** @type {object[]} */
    const bumpLines = [];
    let hasPackExpert = false;
    let baseCalcul = 0;

    for (const item of items) {
      const handle = String(item.handle || '');
      const variantId = toId(item.variant_id);
      const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
      const isBrush = handle === brushHandle || brushVariantIds.has(variantId);
      const isGift = variantId === giftId || Boolean(properties[giftProperty]);
      const isBump = variantId === bumpId;

      if (packHandle && handle === packHandle) hasPackExpert = true;

      if (isGift) giftLines.push(item);
      if (isBump) bumpLines.push(item);

      // Seuil = montant réellement payé, hors brosse (pas les prix barrés).
      if (isBrush) continue;

      baseCalcul += Number(item.final_line_price ?? item.line_price) || 0;
    }

    return {
      hasPackExpert,
      baseCalcul,
      thresholdCents: Number(config.thresholdCents) || 0,
      giftLines,
      bumpLines,
      giftQuantity: giftLines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0),
      bumpQuantity: bumpLines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0),
      giftId,
      bumpId,
    };
  }

  /**
   * @param {{ type: string, state: string, snapshot: object }} plan
   */
  async #execute(plan) {
    const { snapshot } = plan;

    if (plan.type === 'remove-bump') {
      return this.#updateVariantQuantity(snapshot.bumpId, 0, 'remove', snapshot.bumpLines);
    }
    if (plan.type === 'remove-gift') {
      return this.#updateVariantQuantity(snapshot.giftId, 0, 'remove', snapshot.giftLines);
    }
    if (plan.type === 'clamp-gift') {
      return this.#updateVariantQuantity(snapshot.giftId, 1, 'update', snapshot.giftLines);
    }
    if (plan.type === 'add-gift') {
      return this.#addVariant(snapshot.giftId, 1, { [this.#config.giftProperty || '_offert']: 'true' });
    }

    return this.#fetchCart();
  }

  /**
   * Order bump : ajout explicite uniquement (jamais automatique).
   * @param {Element} button
   */
  async #addBump(button) {
    this.#setBumpLoading(button, true);

    try {
      const cart = await this.#fetchCart();
      const plan = this.#plan(cart);
      if (plan.state !== 'PAYANTE') {
        this.#updateBumpUi(plan);
        return;
      }
      if (plan.snapshot.bumpQuantity > 0) {
        this.#updateBumpUi(plan);
        return;
      }

      const nextCart = await this.#addVariant(this.#config.variantIds.bump, 1, {});
      this.#updateBumpUi(this.#plan(nextCart));
    } finally {
      this.#setBumpLoading(button, false);
    }
  }

  /**
   * @param {number} variantId
   * @param {number} quantity
   * @param {Record<string, string>} properties
   */
  async #addVariant(variantId, quantity, properties) {
    const id = toId(variantId);
    const payload = {
      items: [
        {
          id,
          quantity,
          properties,
        },
      ],
      sections: this.#sectionIds(),
      sections_url: window.location.pathname,
    };

    return this.#mutate({
      url: Theme.routes.cart_add_url,
      body: payload,
      action: 'add',
      lines: [{ merchandiseId: String(id), quantity }],
    });
  }

  /**
   * @param {number} variantId
   * @param {number} quantity
   * @param {'remove' | 'update'} action
   * @param {object[]} lines
   */
  async #updateVariantQuantity(variantId, quantity, action, lines) {
    const id = toId(variantId);
    const payload = {
      updates: { [String(id)]: quantity },
      sections: this.#sectionIds(),
      sections_url: window.location.pathname,
    };

    return this.#mutate({
      url: Theme.routes.cart_update_url,
      body: payload,
      action,
      lines: (lines || []).map((line) => ({ id: String(line.key || id), quantity })),
    });
  }

  /**
   * Une seule requête panier à la fois : appelée uniquement depuis la file.
   * @param {{ url: string, body: object, action: 'add' | 'remove' | 'update', lines: object[] }} request
   */
  async #mutate(request) {
    this.#internalMutation = true;
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: request.action,
        context: 'cart',
        lines: request.lines,
        promise: deferred.promise,
        detail: { source: SOURCE },
      })
    );

    try {
      const response = await fetch(request.url, fetchConfig('json', { body: JSON.stringify(request.body) }));
      const data = await response.json();

      if (data.errors || data.status) {
        const message = typeof data.errors === 'string' ? data.errors : data.description || data.message || 'Erreur panier';
        throw new Error(message);
      }

      const fullCart = this.#isAjaxCart(data) ? data : await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
        detail: {
          source: SOURCE,
          sections: data.sections,
          didError: false,
        },
      });

      // Laisse Horizon morpher le drawer avant le prochain calcul.
      await waitFrames(2);
      return this.#isAjaxCart(data) ? data : fullCart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    } finally {
      this.#internalMutation = false;
    }
  }

  /**
   * @param {object} data
   */
  #isAjaxCart(data) {
    return Boolean(data && Array.isArray(data.items) && typeof data.item_count === 'number');
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Lecture panier impossible (${response.status})`);
    return response.json();
  }

  #sectionIds() {
    const ids = new Set();
    if (this.#config?.sectionId) ids.add(this.#config.sectionId);

    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) {
        ids.add(node.dataset.sectionId);
      }
    });

    return Array.from(ids).join(',');
  }

  /**
   * @param {{ state: string, snapshot: object }} plan
   */
  #updateBumpUi(plan) {
    const show =
      this.#config.showBump !== false && plan.state === 'PAYANTE' && plan.snapshot.bumpQuantity === 0;

    document.querySelectorAll(BUMP_SELECTOR).forEach((node) => {
      if (show) node.removeAttribute('hidden');
      else node.setAttribute('hidden', '');
    });
  }

  /**
   * @param {Element} button
   * @param {boolean} loading
   */
  #setBumpLoading(button, loading) {
    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', '');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
    }
  }
}

if (!window.__kalaboostCartBrush) {
  window.__kalaboostCartBrush = new KalaboostCartBrush();
}
